import { useCallback, useEffect, useRef } from "react";

import { landingPageAssets } from "./assets";
import type { AudioTuningSettings } from "./audio-tuning";

function createAudio(source: string, options?: { loop?: boolean; volume?: number }) {
  const audio = new Audio(source);

  audio.loop = options?.loop ?? false;
  audio.preload = "auto";
  audio.volume = options?.volume ?? 1;

  return audio;
}

function shouldUsePreAttenuatedBackgroundMusic() {
  if (typeof navigator === "undefined") {
    return false;
  }

  const isMobileDevice =
    /Android|iPad|iPhone|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const hasCoarsePointer =
    typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;

  return isMobileDevice || hasCoarsePointer;
}

function playFromStart(audio: HTMLAudioElement) {
  audio.pause();
  audio.currentTime = 0;
  void audio.play();
}

export function useSeedPacketAudio(settings: AudioTuningSettings) {
  const usesPreAttenuatedBackgroundRef = useRef(false);
  const backgroundMusicRef = useRef<HTMLAudioElement | null>(null);
  const revealRef = useRef<HTMLAudioElement | null>(null);
  const revealFadeFrameRef = useRef<number | null>(null);
  const revealStartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const revealStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settingsRef = useRef(settings);
  const tapOneRef = useRef<HTMLAudioElement | null>(null);
  const tapTwoRef = useRef<HTMLAudioElement | null>(null);
  const uiHoverRef = useRef<HTMLAudioElement | null>(null);

  const clearRevealSchedule = useCallback(() => {
    if (revealStartTimerRef.current) {
      clearTimeout(revealStartTimerRef.current);
      revealStartTimerRef.current = null;
    }
    if (revealStopTimerRef.current) {
      clearTimeout(revealStopTimerRef.current);
      revealStopTimerRef.current = null;
    }
    if (revealFadeFrameRef.current !== null) {
      cancelAnimationFrame(revealFadeFrameRef.current);
      revealFadeFrameRef.current = null;
    }
  }, []);

  useEffect(() => {
    usesPreAttenuatedBackgroundRef.current = shouldUsePreAttenuatedBackgroundMusic();
    const backgroundMusicSource = usesPreAttenuatedBackgroundRef.current
      ? landingPageAssets.audio.backgroundMusicMobile
      : landingPageAssets.audio.backgroundMusic;

    backgroundMusicRef.current = createAudio(backgroundMusicSource, {
      loop: true,
      volume: usesPreAttenuatedBackgroundRef.current ? 1 : settingsRef.current.backgroundVolume,
    });
    revealRef.current = createAudio(landingPageAssets.audio.reveal, {
      volume: settingsRef.current.revealVolume,
    });
    tapOneRef.current = createAudio(landingPageAssets.audio.tapOne, {
      volume: settingsRef.current.tapOneVolume,
    });
    tapTwoRef.current = createAudio(landingPageAssets.audio.tapTwo, {
      volume: settingsRef.current.tapTwoVolume,
    });
    uiHoverRef.current = createAudio(landingPageAssets.audio.uiHover, { volume: 0.42 });

    return () => {
      clearRevealSchedule();
      for (const audio of [
        backgroundMusicRef.current,
        revealRef.current,
        tapOneRef.current,
        tapTwoRef.current,
        uiHoverRef.current,
      ]) {
        audio?.pause();
      }
    };
  }, [clearRevealSchedule]);

  useEffect(() => {
    settingsRef.current = settings;

    if (backgroundMusicRef.current) {
      backgroundMusicRef.current.volume = usesPreAttenuatedBackgroundRef.current
        ? 1
        : settings.backgroundVolume;
    }
    if (revealRef.current) {
      revealRef.current.volume = settings.revealVolume;
      revealRef.current.playbackRate = settings.revealPlaybackRate;
    }
    if (tapOneRef.current) {
      tapOneRef.current.volume = settings.tapOneVolume;
    }
    if (tapTwoRef.current) {
      tapTwoRef.current.volume = settings.tapTwoVolume;
    }
  }, [settings]);

  const playReveal = useCallback(() => {
    const reveal = revealRef.current;

    if (!reveal) {
      return;
    }

    clearRevealSchedule();
    reveal.pause();
    reveal.currentTime = 0;

    const startReveal = () => {
      const currentSettings = settingsRef.current;
      const playbackRate = Math.max(currentSettings.revealPlaybackRate, 0.01);
      const cutoffMs = (currentSettings.revealCutoffSeconds / playbackRate) * 1000;
      const fadeDurationMs = Math.min(currentSettings.revealFadeOutMs, cutoffMs);
      const fadeStartMs = Math.max(cutoffMs - fadeDurationMs, 0);

      reveal.playbackRate = playbackRate;
      reveal.volume = currentSettings.revealVolume;
      playFromStart(reveal);

      revealStopTimerRef.current = setTimeout(() => {
        const fadeStartedAt = performance.now();
        const fade = () => {
          const progress =
            fadeDurationMs <= 0
              ? 1
              : Math.min((performance.now() - fadeStartedAt) / fadeDurationMs, 1);
          reveal.volume = currentSettings.revealVolume * (1 - progress);

          if (progress < 1) {
            revealFadeFrameRef.current = requestAnimationFrame(fade);
            return;
          }

          reveal.pause();
          reveal.currentTime = 0;
          reveal.volume = settingsRef.current.revealVolume;
          revealFadeFrameRef.current = null;
        };

        fade();
      }, fadeStartMs);
    };

    if (settingsRef.current.revealDelayMs > 0) {
      revealStartTimerRef.current = setTimeout(startReveal, settingsRef.current.revealDelayMs);
    } else {
      startReveal();
    }
  }, [clearRevealSchedule]);

  return {
    playBackgroundMusic: () => {
      const backgroundMusic = backgroundMusicRef.current;

      if (!backgroundMusic) {
        return;
      }

      void backgroundMusic.play();
    },
    playReveal,
    playUiHover: () => {
      const uiHover = uiHoverRef.current;

      if (!uiHover) {
        return;
      }

      uiHover.pause();
      uiHover.currentTime = 0;
      void uiHover.play().catch(() => undefined);
    },
    playTapOne: () => {
      const tapOne = tapOneRef.current;

      if (tapOne) {
        playFromStart(tapOne);
      }
    },
    playTapTwo: () => {
      const tapTwo = tapTwoRef.current;

      if (tapTwo) {
        playFromStart(tapTwo);
      }
    },
  };
}
